<template>
    <div class="space-y-4">
        <!-- Visitor Type -->
        <div class="flex flex-col gap-2">
            <label class="font-medium">
                {{ t('calendar.new.visitors.type') }} <span class="text-red-500">*</span>
            </label>
            <Select :modelValue="visitor.type" @update:modelValue="updateVisitorField('type', $event)"
                :options="visitorTypeOptions" optionLabel="label" optionValue="value" class="w-full"
                @change="$emit('touch-field', 'visitorType')" />
        </div>

        <!-- Common fields for Non-Professional and New Professional -->
        <template v-if="visitor.type !== VisitorType.ErpProfessional">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.firstName') }} <span class="text-red-500">*</span>
                    </label>
                    <InputText :modelValue="visitor.firstName"
                        @update:modelValue="updateVisitorField('firstName', $event)" class="w-full"
                        @blur="$emit('touch-field', 'visitorFirstName')" />
                    <small v-if="validationErrors.firstName" class="text-red-500">
                        {{ validationErrors.firstName }}
                    </small>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.lastName') }} <span class="text-red-500">*</span>
                    </label>
                    <InputText :modelValue="visitor.lastName"
                        @update:modelValue="updateVisitorField('lastName', $event)" class="w-full"
                        @blur="$emit('touch-field', 'visitorLastName')" />
                    <small v-if="validationErrors.lastName" class="text-red-500">
                        {{ validationErrors.lastName }}
                    </small>
                </div>
            </div>

            <div class="flex flex-col gap-2">
                <label class="font-medium">
                    {{ t('calendar.new.visitors.email') }} <span class="text-red-500">*</span>
                </label>
                <InputText :modelValue="visitor.email" @update:modelValue="updateVisitorField('email', $event)"
                    type="email" class="w-full" @blur="$emit('touch-field', 'visitorEmail')" />
                <small class="text-sm text-gray-500">
                    {{ t('calendar.new.visitors.emailHelperText') }}
                </small>
                <small v-if="validationErrors.email" class="text-red-500">
                    {{ validationErrors.email }}
                </small>
            </div>
        </template>

        <!-- Fields for Non-Professional -->
        <template v-if="visitor.type === VisitorType.NonProfessional">
            <Divider />
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.role') }}
                    </label>
                    <InputText :modelValue="visitor.role" @update:modelValue="updateVisitorField('role', $event)"
                        class="w-full" />
                </div>

                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.activitySector') }}
                    </label>
                    <InputText :modelValue="visitor.activitySector"
                        @update:modelValue="updateVisitorField('activitySector', $event)" class="w-full" />
                </div>
            </div>
        </template>

        <!-- Fields for New Professional -->
        <template v-if="visitor.type === VisitorType.NewProfessional">
            <Divider />
            <div class="flex flex-col gap-2">
                <label class="font-medium">
                    {{ t('calendar.new.visitors.country') }} <span class="text-red-500">*</span>
                </label>
                <Select :modelValue="visitor.country" @update:modelValue="updateVisitorField('country', $event)"
                    :options="countries" optionLabel="name" class="w-full" :loading="countriesLoading" filter
                    @change="$emit('touch-field', 'visitorCountry')" />
                <small v-if="validationErrors.country" class="text-red-500">
                    {{ validationErrors.country }}
                </small>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.commercialSegment') }} <span class="text-red-500">*</span>
                    </label>
                    <Select :modelValue="visitor.commercialSegment"
                        @update:modelValue="updateVisitorField('commercialSegment', $event)" filter
                        :options="commercialSegments" optionLabel="name" class="w-full"
                        :loading="commercialSegmentsLoading"
                        @change="$emit('touch-field', 'visitorCommercialSegment')" />
                    <small v-if="validationErrors.commercialSegment" class="text-red-500">
                        {{ validationErrors.commercialSegment }}
                    </small>
                </div>

                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.businessName') }}
                    </label>
                    <InputText :modelValue="visitor.businessName"
                        @update:modelValue="updateVisitorField('businessName', $event)" class="w-full" />
                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.functionary') }}
                    </label>
                    <Select :modelValue="selectedFunctionary" @update:modelValue="onFunctionaryChange"
                        :options="functionaries" optionLabel="name" class="w-full" :loading="functionariesLoading"
                        filter showClear>
                        <template #option="slotProps">
                            <div>
                                <div>{{ slotProps.option.name }}</div>
                                <div class="text-muted-color text-sm">{{ slotProps.option.code }}</div>
                            </div>
                        </template>
                    </Select>
                </div>

                <div class="flex flex-col gap-2">
                    <label class="font-medium">
                        {{ t('calendar.new.visitors.agent') }}
                    </label>
                    <Select :modelValue="selectedAgent" @update:modelValue="onAgentChange" :options="agents"
                        optionLabel="name" class="w-full" :loading="agentsLoading" filter showClear
                        :disabled="!selectedFunctionary">
                        <template #option="slotProps">
                            <div>
                                <div>{{ slotProps.option.name }}</div>
                                <div class="text-muted-color text-sm">{{ slotProps.option.code }}</div>
                            </div>
                        </template>
                    </Select>
                </div>
            </div>
        </template>

        <!-- Fields for ERP Professional -->
        <template v-if="visitor.type === VisitorType.ErpProfessional">
            <Divider />
            <div class="flex flex-col gap-2">
                <label class="font-medium">
                    {{ t('calendar.searchCustomerCode') }} <span class="text-red-500">*</span>
                </label>
                <CustomerAutoComplete :modelValue="selectedErpCustomer" @update:modelValue="onCustomerChange"
                    @cliente-selezionato="onCustomerSelected" @blur="$emit('touch-field', 'erpCustomer')" />
                <small v-if="validationErrors.erpCustomer" class="text-red-500">
                    {{ validationErrors.erpCustomer }}
                </small>
            </div>

            <div v-if="selectedErpCustomer" class="p-2 rounded-lg bg-blue-50 border border-blue-200">
                <p class="font-semibold mb-1">{{ selectedErpCustomer.name }}</p>
                <p class="">{{ selectedErpCustomer.code }}</p>
            </div>

            <div class="flex flex-col gap-2">
                <label class="font-medium">
                    {{ t('calendar.new.visitors.email') }} <span class="text-red-500">*</span>
                </label>
                <InputText :modelValue="visitor.email" @update:modelValue="updateVisitorField('email', $event)"
                    type="email" class="w-full" @blur="$emit('touch-field', 'visitorEmail')" />
                <small v-if="validationErrors.email" class="text-red-500">
                    {{ validationErrors.email }}
                </small>
            </div>

            <div class="flex items-center gap-3 rounded-lg">
                <Checkbox :modelValue="visitor.representsPurchaseGroup"
                    @update:modelValue="updateVisitorField('representsPurchaseGroup', $event)" :binary="true"
                    inputId="purchase-group" />
                <label for="purchase-group" class="font-medium cursor-pointer">
                    {{ t('calendar.new.visitors.representsPurchaseGroup') }}
                </label>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import CustomerAutoComplete from '@/components/CustomerAutoComplete.vue';
import { getAgentsByFunctionary, getFunctionaries } from '@/lib/api/customer';
import type { CommercialSegment } from '@/lib/types/commercialSegment';
import type { Country } from '@/lib/types/country';
import { VisitorType, type Visitor } from '@/lib/types/visitor';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
    visitor: Visitor;
    selectedErpCustomer: any;
    countries: Country[];
    countriesLoading: boolean;
    commercialSegments: CommercialSegment[];
    commercialSegmentsLoading: boolean;
    validationErrors: {
        firstName?: string;
        lastName?: string;
        email?: string;
        country?: string;
        commercialSegment?: string;
        erpCustomer?: string;
    };
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'update:visitor': [visitor: Visitor];
    'update:selectedErpCustomer': [customer: any];
    'touch-field': [fieldName: string];
}>();

const visitorTypeOptions = [
    { label: t('visitor.type.nonProfessional'), value: VisitorType.NonProfessional },
    { label: t('visitor.type.newProfessional'), value: VisitorType.NewProfessional },
    { label: t('visitor.type.erpProfessional'), value: VisitorType.ErpProfessional }
];

// Functionary / Agent cascading selects
const functionaries = ref<{ code: string; name: string }[]>([]);
const agents = ref<{ code: string; name: string }[]>([]);
const selectedFunctionary = ref<{ code: string; name: string } | null>(null);
const selectedAgent = ref<{ code: string; name: string } | null>(null);
const functionariesLoading = ref(false);
const agentsLoading = ref(false);

watch(() => props.visitor.type, async (newType) => {
    if (newType === VisitorType.NewProfessional) {
        functionariesLoading.value = true;
        try {
            functionaries.value = await getFunctionaries();
        } finally {
            functionariesLoading.value = false;
        }

        // Restore selections when navigating back with existing data
        if (props.visitor.functionaryId) {
            const match = functionaries.value.find(f => f.code === props.visitor.functionaryId);
            if (match) {
                selectedFunctionary.value = match;

                agentsLoading.value = true;
                try {
                    agents.value = await getAgentsByFunctionary(match.code);
                } finally {
                    agentsLoading.value = false;
                }

                if (props.visitor.agentId) {
                    selectedAgent.value = agents.value.find(a => a.code === props.visitor.agentId) || null;
                }
            }
        }
    } else {
        functionaries.value = [];
        agents.value = [];
        selectedFunctionary.value = null;
        selectedAgent.value = null;
    }
}, { immediate: true });

function onFunctionaryChange(functionary: { code: string; name: string } | null) {
    selectedFunctionary.value = functionary;
    selectedAgent.value = null;
    agents.value = [];

    const updatedVisitor = { ...props.visitor };
    updatedVisitor.functionaryId = functionary?.code || null;
    updatedVisitor.functionaryName = functionary?.name || null;
    updatedVisitor.agentId = null;
    updatedVisitor.agentName = null;
    emit('update:visitor', updatedVisitor);

    if (functionary) {
        agentsLoading.value = true;
        getAgentsByFunctionary(functionary.code)
            .then(res => { agents.value = res; })
            .finally(() => { agentsLoading.value = false; });
    }
}

function onAgentChange(agent: { code: string; name: string } | null) {
    selectedAgent.value = agent;
    const updatedVisitor = { ...props.visitor };
    updatedVisitor.agentId = agent?.code || null;
    updatedVisitor.agentName = agent?.name || null;
    emit('update:visitor', updatedVisitor);
}

function updateVisitorField(field: keyof Visitor, value: any) {
    emit('update:visitor', { ...props.visitor, [field]: value });
}

function onCustomerChange(customer: any) {
    emit('update:selectedErpCustomer', customer);
}

function onCustomerSelected(customer: any) {
    emit('update:selectedErpCustomer', customer);

    if (customer) {
        const updatedVisitor = { ...props.visitor };
        updatedVisitor.firstName = customer.name?.split(' ')[0] || '';
        updatedVisitor.lastName = customer.name?.split(' ').slice(1).join(' ') || '';
        updatedVisitor.email = customer.email || '';
        updatedVisitor.erpCode = customer.code || null;
        emit('update:visitor', updatedVisitor);
    }

    emit('touch-field', 'erpCustomer');
}
</script>
